import type { ApolloClient } from '@apollo/client';
import {
    GetMatchPreferencesDocument,
    type GetMatchPreferencesQuery,
    type GetMatchPreferencesQueryVariables,
} from './components/__generated__/MatchPreferencesSection.generated';
import {
    GetAutoApplyResumesDocument,
    type GetAutoApplyResumesQuery,
} from './resume/__generated__/ResumePage.generated';
import { markProfileModified } from './AutoApplySetupSessionState';

type ProfileEditor =
    | { source: 'profile-preferences-edit'; variables: GetMatchPreferencesQueryVariables }
    | { source: 'profile-resume-edit' };

interface ProfileEditSnapshot {
    editor: ProfileEditor;
    value: string;
}

const SNAPSHOT_KEY = 'autoApplySetup.profileBeforeEdit';

// GraphQL object keys and multi-value preference ordering are not user changes.
const canonicalize = (value: unknown): string => {
    if (Array.isArray(value)) {
        return `[${value.map(canonicalize).sort().join(',')}]`;
    }
    if (value !== null && typeof value === 'object') {
        return `{${Object.entries(value)
            .filter(([key, field]) => key !== '__typename' && field !== undefined)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, field]) => `${JSON.stringify(key)}:${canonicalize(field)}`)
            .join(',')}}`;
    }
    return JSON.stringify(value ?? null);
};

const fetchSnapshot = async (
    client: ApolloClient<object>,
    editor: ProfileEditor,
): Promise<string> => {
    // Read both sides from the server: a cache-first baseline can mistake an old
    // cache entry for an edit. Do not normalize these verification-only responses.
    if (editor.source === 'profile-preferences-edit') {
        const { data } = await client.query<GetMatchPreferencesQuery>({
            query: GetMatchPreferencesDocument,
            variables: editor.variables,
            fetchPolicy: 'no-cache',
            errorPolicy: 'none',
        });
        if (!data.jobSeekerProfileStructuredData) {
            throw new Error('Cannot verify profile preferences');
        }
        return canonicalize(data.jobSeekerProfileStructuredData.preferences);
    }

    const { data } = await client.query<GetAutoApplyResumesQuery>({
        query: GetAutoApplyResumesDocument,
        fetchPolicy: 'no-cache',
        errorPolicy: 'none',
    });
    if (!data.jobSeekerProfile?.profile) {
        throw new Error('Cannot verify profile resume');
    }
    const resume = data.jobSeekerProfile.profile.resume.find(
        (item) => item.resumeType === 'INDEED_RESUME',
    );
    if (resume && resume.dateModified == null) {
        throw new Error('Cannot verify profile resume modification time');
    }
    return canonicalize(resume ? { id: resume.id, dateModified: resume.dateModified } : null);
};

export const captureProfileBeforeEdit = async (
    client: ApolloClient<object>,
    editor: ProfileEditor,
): Promise<void> => {
    try {
        // Never reuse a previous editor's baseline if this read fails.
        sessionStorage.removeItem(SNAPSHOT_KEY);
        const value = await fetchSnapshot(client, editor);
        sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ editor, value }));
    } catch (error) {
        // Editing remains available, but uncertainty must not authorize deletion.
        console.error('Failed to capture profile before editing:', error);
    }
};

export const hasPendingProfileEdit = (): boolean => {
    try {
        return sessionStorage.getItem(SNAPSHOT_KEY) !== null;
    } catch {
        return false;
    }
};

export const verifyProfileEdit = async (client: ApolloClient<object>): Promise<void> => {
    const stored = sessionStorage.getItem(SNAPSHOT_KEY);
    if (!stored) return;
    const snapshot: ProfileEditSnapshot = JSON.parse(stored);
    const current = await fetchSnapshot(client, snapshot.editor);
    // A newer editor visit owns its own baseline. A late response must not consume it.
    if (sessionStorage.getItem(SNAPSHOT_KEY) !== stored) return;
    if (current !== snapshot.value) {
        markProfileModified();
    }
    sessionStorage.removeItem(SNAPSHOT_KEY);
};
